/**
 * Workspace context — single shared source of truth across the app.
 *
 * Mounted once inside the protected route tree. All components read the
 * same list + active workspace, so creating a workspace anywhere
 * propagates to TopNav, Dashboard, BrandKit, etc. instantly.
 *
 * Persistence strategy (prevents refresh flicker):
 *   1. On first render, synchronously seed `workspaces` + `active` from
 *      localStorage — so there's NEVER a frame where `active === null`
 *      for a returning user.
 *   2. In the background, refresh against PB to reconcile stale data.
 *      If the cached active workspace has been deleted on the server,
 *      we fall back to the newest workspace.
 *   3. Realtime subscription keeps the list synced with events from
 *      other tabs / the PB admin.
 *
 * Cache is scoped by auth record id — switching users clears it.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { pbFor } from "@/lib/api/router";
import authStore from "@/lib/auth";
import realtime from "@/lib/realtime";
import type { Workspace } from "@/types";

const ACTIVE_KEY = "canvaloop-active-workspace";
const LIST_KEY   = "canvaloop-workspaces-cache";
/** Bump when the cached shape changes. Cache with an older version gets discarded. */
const CACHE_VERSION = 1;

interface CachedList {
  v: number;
  userId: string;
  items: Workspace[];
  savedAt: number;
}

/** Read the cached list ONLY if it belongs to the current auth record. */
function readCachedList(): Workspace[] {
  try {
    const raw = localStorage.getItem(LIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CachedList;
    if (parsed.v !== CACHE_VERSION) return [];
    if (parsed.userId !== authStore.record?.id) return [];
    return Array.isArray(parsed.items) ? parsed.items : [];
  } catch { return []; }
}

function writeCachedList(items: Workspace[]) {
  try {
    const payload: CachedList = {
      v: CACHE_VERSION,
      userId: authStore.record?.id ?? "",
      items,
      savedAt: Date.now(),
    };
    localStorage.setItem(LIST_KEY, JSON.stringify(payload));
  } catch { /* quota / private-mode — ignore */ }
}

function clearCachedList() {
  try { localStorage.removeItem(LIST_KEY); } catch { /* ignore */ }
}

function readCachedActiveId(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; }
}

function writeCachedActiveId(id: string) {
  try { localStorage.setItem(ACTIVE_KEY, id); } catch { /* ignore */ }
}

interface WorkspaceContextValue {
  workspaces: Workspace[];
  active: Workspace | null;
  loading: boolean;
  /** True if the current `workspaces` list came from cache only (hasn't been
   *  reconciled with the server yet). Pages that care about freshness can
   *  show a subtle revalidating indicator. Most shouldn't. */
  revalidating: boolean;
  error: string | null;
  switchTo: (w: Workspace) => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<WorkspaceContextValue | null>(null);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Synchronous seeding from cache so first render is already populated.
  const cachedList = readCachedList();
  const cachedActiveId = readCachedActiveId();
  const cachedActive = cachedList.find((w) => w.id === cachedActiveId) ?? cachedList[0] ?? null;

  const [workspaces, setWorkspaces] = useState<Workspace[]>(cachedList);
  const [active, setActive]         = useState<Workspace | null>(cachedActive);
  // `loading` is only true on a cold (uncached) first load.
  const [loading, setLoading]       = useState<boolean>(cachedList.length === 0);
  const [revalidating, setRevalidating] = useState<boolean>(cachedList.length > 0);
  const [error, setError]           = useState<string | null>(null);

  // Keep the latest `active` in a ref so refresh() can preserve the user's
  // current pick even if it happens to be missing from the server response
  // briefly (e.g. just-created workspaces racing the list call).
  const activeRef = useRef(active);
  activeRef.current = active;

  const refresh = useCallback(async () => {
    if (!authStore.isValid) {
      setWorkspaces([]);
      setActive(null);
      setLoading(false);
      setRevalidating(false);
      setError(null);
      clearCachedList();
      return;
    }
    // Only show the full-page loader if we have no cached data. Otherwise
    // use the silent `revalidating` flag.
    setRevalidating(true);
    setError(null);
    try {
      const list = await pbFor("workspaces").collection("workspaces").getFullList<Workspace>({
        sort: "-created",
        // Pulling the owner's public profile inline lets the "by <name>"
        // chip render immediately in TopNav for admins (who can read the
        // users collection). For non-admins the expand silently no-ops and
        // the fallback /api/cl/members/labels fills the gap.
        expand: "userId",
      });
      setWorkspaces(list);
      writeCachedList(list);

      // Reconcile the active workspace:
      //   1. Keep the current pick if it still exists.
      //   2. Otherwise fall back to localStorage → list[0] → null.
      const currentId = activeRef.current?.id;
      let next: Workspace | null = null;
      if (currentId) next = list.find((w) => w.id === currentId) ?? null;
      if (!next) {
        const storedId = readCachedActiveId();
        if (storedId) next = list.find((w) => w.id === storedId) ?? null;
      }
      if (!next) next = list[0] ?? null;
      setActive(next);
      if (next) writeCachedActiveId(next.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load workspaces";
      console.error("[useWorkspace] refresh failed:", err);
      setError(msg);
      // Don't wipe cached data on network failure — user can keep working.
    } finally {
      setLoading(false);
      setRevalidating(false);
    }
  }, []);

  // Initial load + reload on auth change.
  useEffect(() => {
    void refresh();
    return authStore.onChange(() => { void refresh(); });
  }, [refresh]);

  // Realtime — keep the list synced with PB events from any tab.
  useEffect(() => {
    if (!authStore.isValid) return;
    return realtime.subscribe<Workspace>("workspaces", (e) => {
      setWorkspaces((prev) => {
        let next: Workspace[];
        if (e.action === "delete") {
          next = prev.filter((w) => w.id !== e.record.id);
        } else {
          const idx = prev.findIndex((w) => w.id === e.record.id);
          if (idx === -1) next = [e.record, ...prev];
          else {
            next = [...prev];
            next[idx] = e.record;
          }
        }
        writeCachedList(next);
        return next;
      });
      // Post-signup empty-state: if there's no active yet, adopt the new one.
      if (e.action === "create") {
        setActive((prev) => {
          if (prev) return prev;
          writeCachedActiveId(e.record.id);
          return e.record;
        });
      }
      // Active was deleted elsewhere → pick another.
      if (e.action === "delete" && activeRef.current?.id === e.record.id) {
        setActive((prev) => {
          if (prev?.id !== e.record.id) return prev;
          // Find a replacement in the (already-updated) list. Since setWorkspaces
          // has just run, read via a callback on next tick.
          return null; // will be filled by the next setWorkspaces/refresh
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStore.isValid]);

  // If active becomes null but a list exists, auto-pick the newest.
  useEffect(() => {
    if (!active && workspaces.length > 0) {
      setActive(workspaces[0]);
      writeCachedActiveId(workspaces[0].id);
    }
  }, [active, workspaces]);

  const switchTo = useCallback((w: Workspace) => {
    setActive(w);
    writeCachedActiveId(w.id);
  }, []);

  return (
    <Ctx.Provider value={{ workspaces, active, loading, revalidating, error, switchTo, refresh }}>
      {children}
    </Ctx.Provider>
  );
};

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useWorkspace must be used inside <WorkspaceProvider>");
  }
  return ctx;
}
